import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { dark, toggle } = useTheme();

  const scrollToSection = (id) => {
    setIsOpen(false);
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Logo-Klick: immer im Hero (oben) landen — auch von Unterseiten.
  const goHome = () => {
    setIsOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      // nach dem Routenwechsel sicher nach oben (mehrere Versuche, da Render dauern kann)
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 50);
      });
    }
  };

  return (
    <nav className="bg-white dark:bg-navy-900 shadow-sm dark:shadow-navy-800 sticky top-0 z-50 border-b border-stone-100 dark:border-navy-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); goHome(); }}
              className="flex items-center gap-3 text-2xl font-serif font-bold text-brand dark:text-amber-400"
            >
              <img src="/logo-header.png" alt="Liedzeit Logo" className="h-10 w-10 rounded-full" />
              Liedzeit
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-8 text-sm font-semibold">
            <button
              onClick={() => scrollToSection('ueber')}
              className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors"
            >
              Über Liedzeit
            </button>
            <Link to="/musik" className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors">
              Musik & Konzepte
            </Link>
            <Link to="/textdichter" className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors">
              Verfasser
            </Link>
            <Link to="/kontakt" className="text-stone-600 dark:text-stone-300 hover:text-amber-700 dark:hover:text-amber-400 transition-colors">
              Kontakt
            </Link>

            <button
              onClick={toggle}
              aria-label="Dark Mode umschalten"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 dark:border-navy-700 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-navy-800 transition-colors"
            >
              {dark
                ? <SafeIcon icon={FiSun} className="w-4 h-4 text-amber-400" />
                : <SafeIcon icon={FiMoon} className="w-4 h-4" />
              }
            </button>
          </div>

          {/* Mobile: toggle + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggle}
              aria-label="Dark Mode umschalten"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 dark:border-navy-700 text-stone-500 dark:text-stone-400"
            >
              {dark
                ? <SafeIcon icon={FiSun} className="w-4 h-4 text-amber-400" />
                : <SafeIcon icon={FiMoon} className="w-4 h-4" />
              }
            </button>
            <button onClick={() => setIsOpen(!isOpen)} className="text-stone-600 dark:text-stone-300 hover:text-stone-900 focus:outline-none">
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-7 w-7" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white dark:bg-navy-900 border-t border-stone-100 dark:border-navy-800 shadow-inner">
          <div className="px-4 pt-4 pb-6 space-y-2">
            <button
              onClick={() => scrollToSection('ueber')}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-b border-stone-50 dark:border-navy-800"
            >
              Über Liedzeit
            </button>
            <Link
              to="/musik"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-b border-stone-50 dark:border-navy-800"
            >
              Musik & Konzepte
            </Link>
            <Link
              to="/textdichter"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-b border-stone-50 dark:border-navy-800"
            >
              Verfasser
            </Link>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="block w-full text-left px-3 py-3 text-lg font-serif font-bold text-brand dark:text-amber-400 border-t border-stone-50 dark:border-navy-800"
            >
              Kontakt
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
